/**
 * Users Navigation Sidebar
 * Shows "All Users" and role hierarchy for filtering
 * Styled to match ContentNavigation
 */

import { useState } from 'react';
import { NavLink, useParams } from 'react-router';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
    Users,
    ChevronRight,
    ChevronDown,
    Shield,
    ShieldCheck,
    UserCog,
    Search,
} from 'lucide-react';
import { useRoles } from '@/hooks/access-control';
import type { Role } from '@/stores/access-control';

interface UsersNavigationProps {
    className?: string;
}

export function UsersNavigation({ className }: UsersNavigationProps) {
    const { roleId } = useParams<{ roleId?: string }>();
    const { data: roles = [], isLoading } = useRoles();
    const [search, setSearch] = useState('');

    // Build role hierarchy
    const rootRoles = roles.filter(r => !r.parent);
    const childRolesMap = new Map<string, Role[]>();

    roles.forEach(role => {
        if (role.parent) {
            const children = childRolesMap.get(role.parent) || [];
            children.push(role);
            childRolesMap.set(role.parent, children);
        }
    });

    // Filter roles based on search
    const filteredRoles = search
        ? rootRoles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
        : rootRoles;

    // Show search if more than 10 roles
    const showSearch = roles.length > 10;

    return (
        <div className={cn('flex h-full w-60 flex-col border-r bg-card', className)}>
            {/* Search */}
            {showSearch && (
                <div className="border-b p-3">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search roles..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                </div>
            )}

            <ScrollArea className="flex-1">
                <div className="p-2 space-y-0.5">
                    {/* All Users Link */}
                    <NavLink
                        to="/admin/users"
                        end
                        className={({ isActive }) =>
                            cn(
                                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                                isActive && !roleId
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            )
                        }
                    >
                        <Users className="h-4 w-4 shrink-0" />
                        <span className="truncate">All Users</span>
                    </NavLink>

                    {/* Roles Section */}
                    <div className="mt-4">
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Roles
                        </div>

                        {isLoading ? (
                            <div className="space-y-1 px-1">
                                <Skeleton className="h-8 w-full rounded-md" />
                                <Skeleton className="h-8 w-full rounded-md" />
                                <Skeleton className="h-8 w-full rounded-md" />
                            </div>
                        ) : filteredRoles.length === 0 ? (
                            <div className="px-3 py-8 text-center">
                                <Shield className="mx-auto h-8 w-8 text-muted-foreground/50" />
                                <p className="mt-2 text-sm text-muted-foreground">
                                    {search ? 'No roles found' : 'No roles yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-0.5">
                                {filteredRoles.map(role => (
                                    <RoleNavItem
                                        key={role.id}
                                        role={role}
                                        childRoles={childRolesMap}
                                        activeRoleId={roleId}
                                        level={0}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}

interface RoleNavItemProps {
    role: Role;
    childRoles: Map<string, Role[]>;
    activeRoleId?: string;
    level: number;
}

function RoleNavItem({ role, childRoles, activeRoleId, level }: RoleNavItemProps) {
    const [expanded, setExpanded] = useState(true);
    const children = childRoles.get(role.id) || [];
    const hasChildren = children.length > 0;
    const isActive = activeRoleId === role.id;

    const getRoleIcon = () => {
        // Check role name for admin access indication
        const roleName = role.name.toLowerCase();
        if (roleName.includes('admin')) return <ShieldCheck className="h-4 w-4 shrink-0" />;
        if (roleName.includes('user') || roleName.includes('editor')) return <UserCog className="h-4 w-4 shrink-0" />;
        return <Shield className="h-4 w-4 shrink-0" />;
    };

    return (
        <div>
            <div
                className={cn(
                    'flex items-center rounded-md transition-colors',
                    isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
                style={{ paddingLeft: `${level * 12 + 4}px` }}
            >
                {hasChildren ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 p-0 shrink-0"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                    </Button>
                ) : (
                    <div className="w-6" /> // Spacer for alignment
                )}

                <NavLink
                    to={`/admin/users/roles/${role.id}`}
                    className={cn(
                        'flex-1 flex items-center gap-3 py-2 pr-3 text-sm',
                        isActive && 'font-medium'
                    )}
                >
                    {getRoleIcon()}
                    <span className="truncate">{role.name}</span>
                </NavLink>
            </div>

            {hasChildren && expanded && (
                <div className="space-y-0.5">
                    {children.map(child => (
                        <RoleNavItem
                            key={child.id}
                            role={child}
                            childRoles={childRoles}
                            activeRoleId={activeRoleId}
                            level={level + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
